import { useMemo, useEffect, useState } from 'react';

import Head from 'next/head';
import { NextSeo } from 'next-seo';

import orderApi from '@/apis/order';
import soApi from '@/apis/so';
import staticdata, { resourceCatalogList } from '@/apis/staticdata';
import wwwApi from '@/apis/www';
import IndexRate from '@/components/IndexRate';
import Global from '@/features/global';
import BaseLayout from '@/layouts/baseLayout';
import { baseURL } from '@/utils/baseUrl';
import { getPaginationInfo } from '@/utils/common';
import ecpCurl, { setContext, asyncAll } from '@/utils/ecpCurl';
import { getGlobalSchema } from '@/utils/schema';
import Utils from '@/utils/Utils';
const GlobalPage = (props) => {
  const {
    checkQuery,
    soData,
    resourceData,
    websiteSectionList,
    headerAd,
    paginationInfo,
    isMro,
    searchSource,
    mainMro,
  } = props;
  const [isLgScreen, setIsLgScreen] = useState(null);
  const [catalogList, setCatalogList] = useState(null);

  const globalSearchKeyword = useMemo(() => {
    return Utils.safeDecodeURI(soData?.searchResult?.globalSearchKeyword || '');
  }, [soData]);

  const metaData = useMemo(() => {
    if (!globalSearchKeyword) {
      return {
        title: '立创商城_一站式电子元器件和MRO工业品采购商城_嘉立创电子商城',
        description:
          '立创商城-电子元器件和MRO工业品一站式采购商城，拥有13万+平米智能化仓储，现货SKU品类超64万种，正品保证，支持一键BOM配单，最快4小时发货，为用户提供一站式元器件和工业品线上采购服务。',
        additionalMetaTags: [
          {
            name: 'keywords',
            content: '立创商城,元器件,电子元器件,电子元器件商城,电子元器件采购网,MRO工业品,MRO平台',
          },
          {
            name: 'robots',
            content: 'noindex, follow',
          },
        ],
      };
    }
    const pageTitle = paginationInfo?.currentPage === 1 ? '' : `-第${paginationInfo?.currentPage}页`;
    return {
      title: `${globalSearchKeyword}_${globalSearchKeyword}采购信息-立创电子元器件商城${pageTitle}`,
      description: `立创商城-专业的电子元器件现货采购平台,为您提供${globalSearchKeyword}的价格，库存，数据手册，参数等信息，采购${globalSearchKeyword}上立创商城。`,
      additionalMetaTags: [
        {
          name: 'keywords',
          content: `${globalSearchKeyword}，${globalSearchKeyword}采购，${globalSearchKeyword}库存`,
        },
        {
          name: 'robots',
          content: 'noindex, follow',
        },
      ],
    };
  }, [checkQuery, paginationInfo, globalSearchKeyword]);

  useEffect(() => {
    resourceCatalogList().then((res) => {
      setCatalogList(res?.result);
    });
  }, []);

  const changeIsLgScreen = (value) => {
    setIsLgScreen(value);
  };

  const { JsonLd, MicroData } = getGlobalSchema(soData, resourceData);

  return (
    <>
      <NextSeo
        title={metaData.title}
        description={metaData.description}
        additionalMetaTags={metaData.additionalMetaTags}
      ></NextSeo>
      <Head>
        {paginationInfo?.currentPage !== 1 && paginationInfo?.countPage !== 1 && (
          <link rel="prev" href={paginationInfo?.prevHref} />
        )}
        <link rel="canonical" href={`${resourceData?.projectConfigVO?.lcscSearchUrl}/global.html`} />
        {paginationInfo?.currentPage !== paginationInfo?.countPage && paginationInfo?.countPage !== 1 && (
          <link rel="next" href={paginationInfo?.nextHref} />
        )}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JsonLd) }} />
      </Head>
      {MicroData}
      <BaseLayout
        resourceData={resourceData}
        catalogList={catalogList}
        websiteSectionList={websiteSectionList}
        headerAd={headerAd}
        isLgScreen={isLgScreen}
        notIndexHeaderAdapter={true}
        isNeedTopAd={true}
        isShowBottomAd={true}
        globalSearchKeyword={globalSearchKeyword}
        showMemberWebsite={true}
        showFriendWebsite={false}
      >
        <IndexRate requestPage="search" />
        <Global
          {...soData}
          paginationInfo={paginationInfo}
          isMro={isMro}
          mainMro={mainMro}
          riskMsg={checkQuery?.riskMsg}
          globalSearchKeyword={globalSearchKeyword}
          searchSource={searchSource}
          changeIsLgScreen={changeIsLgScreen}
        ></Global>
      </BaseLayout>
    </>
  );
};

export async function getServerSideProps(ctx) {
  setContext(ctx);
  const { query, req } = ctx;
  // xss攻击校验
  if (Utils.isXss(query?.k)) {
    return {
      notFound: true,
    };
  }
  const keyword = encodeURIComponent(query?.k || '');
  const isMro = false;
  const mainMro = query?.searchSource === 'main_mro';
  const [
    checkQuery,
    soData,
    defaultAddressBelongWarehouse,
    wwwStockpileHistorySwitch,
    resourceData,
    websiteSectionList,
    headerAd,
    notFound,
  ] = await asyncAll([
    ecpCurl(soApi.checkQuery, { query: { keyword } }),
    ecpCurl(isMro || mainMro ? soApi.mroGlobal : soApi.global, {
      query: { keyword, searchSource: query?.searchSource },
    }),
    ecpCurl(orderApi.selectWarehouse, {}, 'sz'),
    ecpCurl(wwwApi.wwwStockpileHistorySwitch, {}, 'yes'),
    ecpCurl(staticdata.resourceList, { enableCache: true }),
    ecpCurl(staticdata.websiteSection, { enableCache: true }),
    ecpCurl(staticdata.advertisingHeader, { enableCache: true }),
  ]);
  if (notFound) {
    return {
      notFound: true,
    };
  }

  if (soData?.searchResult) {
    soData.searchResult.defaultAddressBelongWarehouse = defaultAddressBelongWarehouse || 'sz';
  }

  if (soData && wwwStockpileHistorySwitch?.switch) {
    soData.showStockBtn = wwwStockpileHistorySwitch?.switch === 'yes';
  }

  const originalUrl = req.url;
  const currentUrl = `${baseURL}${originalUrl.replace('global', 'global.html')}`;
  const currentPage = query.page ? Number(query.page) : 1;
  return {
    props: {
      checkQuery,
      resourceData,
      websiteSectionList,
      headerAd,
      soData,
      query,
      isMro,
      mainMro,
      searchSource: query.searchSource || '',
      paginationInfo: getPaginationInfo({
        currentUrl,
        currentPage,
        countPage:
          soData?.searchResult?.countPage ||
          parseInt(soData?.searchResult?.totalCount / soData?.searchResult?.pageSize),
        totalCount: soData?.searchResult?.totalCount,
        pageSize: soData?.searchResult?.pageSize,
      }),
    },
  };
}

export default GlobalPage;
